import React from 'react';
import ImageSelector from '../components/ImageSelector';
import NodeEditorAction from '../components/NodeEditorAction';
import { PanoramaVFTInfospotConfig } from '../models/PanoramaVFTConfig';
import SelectDropdown from '../components/SelectDropdown';

type PanoramaVFTInfospotEditorProps = {
    infospot: PanoramaVFTInfospotConfig,
    nodeKey: number,
    availableScenes: Map<string, string>,
    availablePortals: Map<string, string>,
    updateInfospot: (i: PanoramaVFTInfospotConfig, key: number) => void,
    onChevronUp: (key: number) => void,
    onChevronDown: (key: number) => void,
    onDelete: (key: number) => void,
};

const PanoramaVFTInfospotEditor = ({
    infospot,
    nodeKey,
    availableScenes,
    availablePortals,
    updateInfospot,
    onChevronUp,
    onChevronDown,
    onDelete }: PanoramaVFTInfospotEditorProps) => {

    const labelClasses = "block text-left tracking-wide text-gray-700 text-xs font-bold mb-2";
    const inputClasses = "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500";

    const onTypeChange = (newType: string) => {
        infospot.eventType = newType;
        updateInfospot(infospot, nodeKey);
    }

    const onNameChange = (e: React.FormEvent<HTMLInputElement>) => {
        infospot.eventName = e.currentTarget.value;
        updateInfospot(infospot, nodeKey);
    }

    const onSceneChange = (newScene: string) => {
        infospot.scene = newScene;
        updateInfospot(infospot, nodeKey);
    }

    const onUnvisitedAssetChange = (src: string) => {
        infospot.sourceAsset = src;
        updateInfospot(infospot, nodeKey);
    }

    const onSelectedAssetChange = (src: string) => {
        infospot.selectedAsset = src;
        updateInfospot(infospot, nodeKey);
    }

    const onVisitedAssetChange = (src: string) => {
        infospot.visitedAsset = src;
        updateInfospot(infospot, nodeKey);
    }

    const onTargetSceneChange = (newPortal: string) => {
        infospot.target = newPortal;
        updateInfospot(infospot, nodeKey);
    }

    const onInfotextChange = (e: React.FormEvent<HTMLInputElement>) => {
        infospot.infoText = e.currentTarget.value;
        updateInfospot(infospot, nodeKey);
    }

    const onInfotextDeltaChange = (e: React.FormEvent<HTMLInputElement>) => {
        infospot.infoTextDelta = Number(e.currentTarget.value);
        updateInfospot(infospot, nodeKey);
    }

    const onXChange = (e: React.FormEvent<HTMLInputElement>) => {
        infospot.x = Number(e.currentTarget.value);
        updateInfospot(infospot, nodeKey);
    }

    const onYChange = (e: React.FormEvent<HTMLInputElement>) => {
        infospot.y = Number(e.currentTarget.value);
        updateInfospot(infospot, nodeKey);
    }

    const onZChange = (e: React.FormEvent<HTMLInputElement>) => {
        infospot.z = Number(e.currentTarget.value);
        updateInfospot(infospot, nodeKey);
    }

    const infoTypeOptions = new Map<string, string>([["hotspot", "Hotspot"], ["portal", "Portal"]]);

    return (
        <div className='mb-3 border rounded-lg shadow'>
            <div className="px-2 my-2 flex flex-wrap">
                <div className="flex-1 px-3">
                    <div className="flex flex-wrap -mx-3 mb-4">
                        <div className="w-full md:w-1/2 px-3">
                            <label className={labelClasses}>Type</label>
                            <SelectDropdown
                                value={infospot.eventType}
                                options={infoTypeOptions}
                                onSelectChange={onTypeChange} />
                        </div>
                        <div className="w-full md:w-1/2 px-3">
                            <label className={labelClasses}>Unique ID</label>
                            <input
                                className={inputClasses}
                                value={infospot.eventName}
                                onChange={onNameChange}
                                placeholder='Enter the Unique ID of the infospot' />
                        </div>
                    </div>
                    <div className='mb-4'>
                        <label className={labelClasses}>Scene</label>
                        <SelectDropdown
                            value={infospot.scene}
                            options={availableScenes}
                            onSelectChange={onSceneChange} />
                    </div>
                    <div className='mb-4'>
                        <label className={labelClasses}>Tooltip</label>
                        <input
                            className={inputClasses}
                            value={infospot.infoText}
                            onChange={onInfotextChange}
                            placeholder='Enter the tooltip of the infospot' />
                    </div>
                    <div className='mb-4'>
                        <label className={labelClasses}>Tooltip height</label>
                        <input
                            className={inputClasses}
                            value={infospot.infoTextDelta}
                            onChange={onInfotextDeltaChange}
                            type="number"
                            placeholder='Enter the Y margin of the infospot tooltip' />
                    </div>

                    <div className="flex flex-wrap -mx-3 mb-4">
                        <div className="w-full md:w-1/3 px-3">
                            <label className={labelClasses}>X</label>
                            <input
                                className={inputClasses}
                                value={infospot.x}
                                onChange={onXChange}
                                type="number"
                                placeholder='Enter the x coordinate of the infospot' />
                        </div>
                        <div className="w-full md:w-1/3 px-3">
                            <label className={labelClasses}>Y</label>
                            <input
                                className={inputClasses}
                                value={infospot.y}
                                onChange={onYChange}
                                type="number"
                                placeholder='Enter the y coordinate of the infospot' />
                        </div>
                        <div className="w-full md:w-1/3 px-3">
                            <label className={labelClasses}>Z</label>
                            <input
                                className={inputClasses}
                                value={infospot.z}
                                onChange={onZChange}
                                type="number"
                                placeholder='Enter the z coordinate  of the infospot' />
                        </div>
                    </div>
                    <div className='mb-4'>
                        <label className={labelClasses}>Unvisited asset</label>
                        <ImageSelector value={infospot.sourceAsset} onImageChange={onUnvisitedAssetChange} />
                    </div>
                    {
                        infospot.eventType === "hotspot"
                            ?
                            <>
                                <div className='mb-4'>
                                    <label className={labelClasses}>Selected asset</label>
                                    <ImageSelector value={infospot.selectedAsset} onImageChange={onSelectedAssetChange} />
                                </div>
                                <div className='mb-4'>
                                    <label className={labelClasses}>Visited asset</label>
                                    <ImageSelector value={infospot.visitedAsset} onImageChange={onVisitedAssetChange} />
                                </div>
                            </>
                            :
                            <>
                                <div className='mb-4'>
                                    <label className={labelClasses}>Linked portal</label>
                                    <SelectDropdown
                                        value={infospot.target}
                                        options={availablePortals}
                                        onSelectChange={onTargetSceneChange} />
                                </div>
                            </>
                    }
                </div>

                <NodeEditorAction nodeKey={nodeKey} onChevronUp={onChevronUp} onChevronDown={onChevronDown} onDelete={onDelete} />
            </div>
        </div>
    )
}

export default PanoramaVFTInfospotEditor;

